import { isLikelyTransientError } from '@/utils/errors/isLikelyTransientError';

const contextualizedLogMessage = (message: string) =>
  `RetryFailedCall. ${message}`;

export interface RetryOptions {
  /**
   * Number of retry attempts to attempt.
   */
  retries: number;

  /**
   * Time in milliseconds to wait between retry attempts.
   * Default is 1000 ms.
   */
  retryDelayMs?: number;

  /**
   * Optional custom function to determine whether a caught error is transient and should be retried.
   * If omitted, the default will use the default isLikelyTransientError function.
   */
  isRetryableError?: (err: unknown) => boolean;
}

/**
 * Util that holds logic for retrying failed http calls
 * Usage : Use in catch block after a failed call, this will check wether the error is transient or not and initiate retries if needed
 * Performs a limited number of silent retries, and rethrows the error if result is not transient OR if the all the attempts failed
 * DO NOT USE with high number of retries unless you know you're handling the UI, as this has no UI state management whatsoever
 * Note that in the current implementation, this will ALSO rethrow the error if the last retry attempt failed, even if the error was still transient.
 * So "long-lived" transient errors will ultimately be treated as hard failures
 * With default configuration, this concerns transient errors lasting more than ~5 seconds (not including function call time)
 * TODO - Add persistent retry handling and UI state management or callbacks to handle UI state
 *
 * @param fn - The function to retry. Must return a Promise.
 * @param options - Retry configuration options.
 * @returns A Promise resolving to the function's return value, or rejecting with the final error.
 *
 * @throws - If all retry attempts fail or a non-transient error is encountered.
 */
// TODO Improvement note : make the initialError optional, and usable directly as a first call and not from the catch block. In that case, rename the function to something like withRetries
export async function retryFailedCall<T>(
  fn: () => Promise<T>,
  initialError: unknown,
  options: RetryOptions
): Promise<T> {
  const {
    retries = 25,
    retryDelayMs = 2000,
    isRetryableError = isLikelyTransientError,
  } = options ?? {};

  let lastError = initialError;

  const isTransient = isRetryableError(initialError);

  if (!isTransient) {
    console.debug(
      contextualizedLogMessage('Initial error not retryable. Aborting retry.')
    );
    throw initialError;
  }
  // Silent retry loop
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.debug(
        contextualizedLogMessage(`Silent retry attempt ${attempt}/${retries}`)
      );
      return await fn();
    } catch (err) {
      lastError = err;

      const transient = isRetryableError(err);

      if (!transient || attempt === retries) {
        if (transient && typeof lastError === 'object' && lastError !== null) {
          (lastError as Record<string, unknown>).isTransient = true;
        }

        const failureReason = !transient
          ? 'non-transient detected during retry loop'
          : 'exhausted silent retry count';
        console.debug(
          contextualizedLogMessage(`Silent retry failed : ${failureReason}`)
        );

        break;
      }
      await wait(retryDelayMs);
    }
  }
  // All retries exhausted or non-transient error encountered during retries
  // TODO - Add persistent retry logic and UI state management
  throw lastError;
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
